import { prefStorage } from '@/atoms/storage';
import type { PrefBuilder } from './define';

const MIGRATED_KEY = 'pref:__migrated';

/**
 * One-shot migration from the pre-`pref:*` localStorage layout.
 * Reads the legacy `user` JSON blob and editor_* keys, distributes them
 * to `pref:*` keys, then sets the migration flag.
 *
 * Accepts the builder record so it can drive migration entirely from
 * declared `legacy` and `from` fields, with one bespoke transform for
 * the `editor_sidebar` rename (raw string → JSON).
 */
// biome-ignore lint/suspicious/noExplicitAny: builder record is heterogeneous in T
export function migrateOldPreferences(prefDefs: Record<string, PrefBuilder<any>>): void {
    if (prefStorage.getItem(MIGRATED_KEY)) return;

    migrateUserBlob(prefDefs);
    migrateRenamedKeys(prefDefs);

    prefStorage.setItem(MIGRATED_KEY, '1');
}

// biome-ignore lint/suspicious/noExplicitAny: builder record is heterogeneous in T
function migrateUserBlob(prefDefs: Record<string, PrefBuilder<any>>): void {
    const userJson = prefStorage.getItem('user');
    if (!userJson) return;

    let user: Record<string, unknown>;
    try {
        user = JSON.parse(userJson);
    } catch {
        return;
    }

    for (const [key, builder] of Object.entries(prefDefs)) {
        if (builder.opts.from) continue; // rename path, not from the user blob
        if (!(key in user)) continue;
        const target = `pref:${key}`;
        if (prefStorage.getItem(target) !== null) continue;
        const value = user[key];
        if (value === undefined) continue;
        prefStorage.setItem(target, encodeForLegacy(value, builder.opts.legacy));
    }
}

// biome-ignore lint/suspicious/noExplicitAny: builder record is heterogeneous in T
function migrateRenamedKeys(prefDefs: Record<string, PrefBuilder<any>>): void {
    for (const [key, builder] of Object.entries(prefDefs)) {
        const from = builder.opts.from;
        if (!from) continue;
        const target = `pref:${key}`;
        if (prefStorage.getItem(target) !== null) continue;
        const oldValue = prefStorage.getItem(from);
        if (oldValue === null) continue;
        prefStorage.setItem(target, encodeRenamed(key, oldValue));
    }
}

function encodeForLegacy(value: unknown, legacy: PrefBuilder<unknown>['opts']['legacy']): string {
    if (legacy === 'raw-num' || legacy === 'raw-bool' || legacy === 'raw-str') {
        return value === null ? 'null' : String(value);
    }
    return JSON.stringify(value);
}

function encodeRenamed(key: string, oldRaw: string): string {
    if (key === 'editor_sidebar') {
        return JSON.stringify(oldRaw === 'comments' || oldRaw === 'bits' ? oldRaw : null);
    }
    return oldRaw;
}
